import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import useAuthGuard from '../hooks/useAuthGuard';
import Layout from '../components/Layout';
import { pocApi, percent } from '../utils/pocApi';
import apiFetch from '../utils/apiFetch';
import { API_BASE_URL } from '../config';

// The index of the Video Scan PoC bench.
//
// A dataset is one video's worth of sampled frames. Each row shows how far the
// three phases have got on it, because the phases are sequential in a way the
// screen should make obvious: PH0 needs two annotators before it means
// anything, PH1 needs PH0's boxes to score against, and PH1.5 needs a person to
// have walked the video and listed the machines.

// StoreSearch looks a store up by name. **どの店で撮ったかが、データセットを
// 後から見つけられる唯一の手がかり**。最初の15件のうち9件は店舗なしで作られ、
// 動画のファイル名でしか区別できなくなっていた。
function StoreSearch({ value, onPick, onClear, placeholder }) {
  const [keyword, setKeyword] = useState('');
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const ac = new AbortController();
    const timer = setTimeout(async () => {
      const q = keyword.trim();
      if (!q) {
        setResults([]);
        return;
      }
      setBusy(true);
      try {
        const url = new URL(`${API_BASE_URL}/admin/stores`);
        url.searchParams.set('q', q);
        url.searchParams.set('limit', '8');
        const res = await apiFetch(url.toString(), { signal: ac.signal });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const body = await res.json();
        setResults(Array.isArray(body.data) ? body.data : []);
      } catch (e) {
        if (e.name !== 'AbortError') setResults([]);
      } finally {
        setBusy(false);
      }
    }, 250);
    return () => { clearTimeout(timer); ac.abort(); };
  }, [keyword]);

  if (value) {
    return (
      <div className="d-flex align-items-center gap-2">
        <span className="badge bg-primary-subtle text-primary-emphasis">{value}</span>
        <button type="button" className="btn btn-sm btn-link p-0" onClick={onClear}>変更</button>
      </div>
    );
  }
  return (
    <div className="position-relative">
      <input
        className="form-control form-control-sm"
        placeholder={placeholder || '店舗名で検索'}
        value={keyword}
        onChange={(e) => setKeyword(e.target.value)}
      />
      {busy && <div className="text-muted small">検索中…</div>}
      {results.length > 0 && (
        <div className="list-group position-absolute w-100 shadow-sm" style={{ zIndex: 5 }}>
          {results.map((s) => (
            <button
              key={s.id}
              type="button"
              className="list-group-item list-group-item-action py-1 px-2 text-start"
              onClick={() => { onPick(s); setKeyword(''); setResults([]); }}
            >
              <div className="small fw-semibold">{s.name}</div>
              <div className="text-muted" style={{ fontSize: '0.72rem' }}>{s.prefecture} {s.address}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function PocDatasets() {
  useAuthGuard();

  const [datasets, setDatasets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: '', sourceVideo: '', notes: '' });
  const [videoFile, setVideoFile] = useState(null);
  const [frameCount, setFrameCount] = useState(30);
  const [progress, setProgress] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState('');
  // 一覧の絞り込み。名前・店舗名・動画名のどれに当たってもよい。
  const [filter, setFilter] = useState('');
  // 作成時に選んだ店舗、および行ごとの店舗付け替え。
  const [newStore, setNewStore] = useState(null);
  const [storeEditingId, setStoreEditingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setDatasets((await pocApi.listDatasets()) || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // 店舗で探せるようにする。データセットは増える一方で、作られた順に
  // 並んでいるだけでは「あの店のあれ」に辿り着けない。
  const needle = filter.trim().toLowerCase();
  const shown = needle
    ? datasets.filter((d) =>
        [d.name, d.storeName, d.sourceVideo]
          .filter(Boolean)
          .some((v) => v.toLowerCase().includes(needle)))
    : datasets;

  const create = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setCreating(true);
    setError('');
    setProgress('');
    try {
      const dataset = await pocApi.createDataset({
        ...form,
        storeId: newStore ? newStore.id : undefined,
        sourceVideo: form.sourceVideo || (videoFile ? videoFile.name : ''),
      });

      if (videoFile) {
        // The video goes straight to S3. These are 150-800MB files; sending
        // them through the API would hold a request open for minutes.
        setProgress('動画をアップロード中… 0%');
        const type = videoFile.type || 'video/mp4';
        const urlRes = await apiFetch(
          `${API_BASE_URL}/admin/upload/video-url?content_type=${encodeURIComponent(type)}`
        );
        if (!urlRes.ok) throw new Error(`アップロードURLの取得に失敗しました (${urlRes.status})`);
        const { data } = await urlRes.json();

        await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.upload.addEventListener('progress', (ev) => {
            if (ev.lengthComputable) {
              setProgress(`動画をアップロード中… ${Math.round((ev.loaded / ev.total) * 100)}%`);
            }
          });
          xhr.addEventListener('load', () =>
            (xhr.status === 200 ? resolve() : reject(new Error(`S3への転送に失敗 (${xhr.status})`))));
          xhr.addEventListener('error', () => reject(new Error('通信エラー')));
          xhr.open('PUT', data.uploadUrl);
          xhr.setRequestHeader('Content-Type', type);
          xhr.send(videoFile);
        });

        setProgress('フレームを切り出しています…（数分かかります）');
        const { jobId } = await pocApi.extractFrames(dataset.id, {
          videoUrl: data.publicUrl,
          frames: Number(frameCount) || 30,
        });

        // The sampling runs on the worker; poll until it lands so the operator
        // is not left guessing whether it worked.
        for (let i = 0; i < 120; i += 1) {
          // eslint-disable-next-line no-await-in-loop
          await new Promise((r) => setTimeout(r, 5000));
          // eslint-disable-next-line no-await-in-loop
          const job = await pocApi.jobStatus(jobId);
          if (job.status === 'completed') { setProgress('完了しました'); break; }
          if (job.status === 'failed') throw new Error(job.error || 'フレームの切り出しに失敗しました');
          setProgress(`フレームを切り出しています…（${(i + 1) * 5}秒経過）`);
        }
      }

      setForm({ name: '', sourceVideo: '', notes: '' });
      setVideoFile(null);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setCreating(false);
    }
  };

  const saveName = async (dataset) => {
    const name = editingName.trim();
    if (!name || name === dataset.name) { setEditingId(null); return; }
    try {
      await pocApi.updateDataset(dataset.id, {
        name,
        sourceVideo: dataset.sourceVideo || '',
        notes: dataset.notes || '',
      });
      setDatasets((prev) => prev.map((x) => (x.id === dataset.id ? { ...x, name } : x)));
    } catch (e) {
      setError(e.message);
    } finally {
      setEditingId(null);
    }
  };

  const remove = async (dataset) => {
    // Frames, boxes and the coverage checklist all go with it, and the boxes
    // are hours of somebody's work.
    if (!window.confirm(`「${dataset.name}」とその全フレーム・アノテーションを削除します。よろしいですか？`)) return;
    try {
      await pocApi.deleteDataset(dataset.id);
      await load();
    } catch (e) {
      setError(e.message);
    }
  };

  return (
    <Layout>
      <div className="d-flex justify-content-between align-items-start mb-3">
        <div>
          <h4 className="mb-1">Video Scan PoC</h4>
          <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>
            PH0 ラベル定義 → PH1 検出性能 → PH1.5 物理台カバレッジ を、同じフレーム群の上で測ります。
          </p>
        </div>
      </div>

      {error && <div className="alert alert-danger py-2">{error}</div>}

      <div className="card mb-4">
        <div className="card-body">
          <h6 className="card-title mb-3">データセットを追加</h6>
          <form className="row g-2 align-items-end" onSubmit={create}>
            <div className="col-md-4">
              <label className="form-label small mb-1">名前</label>
              <input
                className="form-control form-control-sm"
                placeholder="GiGO 秋葉原 3号館"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div className="col-md-2">
              <label className="form-label small mb-1">動画（ファイル名など）</label>
              <input
                className="form-control form-control-sm"
                placeholder="IMG_5950.MP4"
                value={form.sourceVideo}
                onChange={(e) => setForm({ ...form, sourceVideo: e.target.value })}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label small mb-1">店舗</label>
              <StoreSearch
                value={newStore ? newStore.name : ''}
                onPick={setNewStore}
                onClear={() => setNewStore(null)}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label small mb-1">メモ</label>
              <input
                className="form-control form-control-sm"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </div>
            <div className="col-md-1">
              <button className="btn btn-primary btn-sm w-100" disabled={creating || !form.name.trim()}>
                追加
              </button>
            </div>
            <div className="row g-2 align-items-end mt-1">
              <div className="col-md-6">
                <label className="form-label small mb-1">動画（任意・そのまま切り出します）</label>
                <input
                  type="file"
                  className="form-control form-control-sm"
                  accept="video/*"
                  onChange={(e) => setVideoFile(e.target.files[0] || null)}
                />
              </div>
              <div className="col-md-2">
                <label className="form-label small mb-1">切り出す枚数</label>
                <input
                  type="number"
                  className="form-control form-control-sm"
                  min={5}
                  max={100}
                  value={frameCount}
                  onChange={(e) => setFrameCount(e.target.value)}
                />
              </div>
            </div>
            {progress && (
              <div className="alert alert-info py-2 px-3 mt-2 mb-0 small">{progress}</div>
            )}
          </form>
          <p className="text-muted mb-0 mt-3" style={{ fontSize: '0.75rem' }}>
            動画を選ばずに作れば、空のデータセットになります。サーバ側の CLI でも切り出せます:
            <code className="ms-1">
              go run ./cmd/poc-extract-frames -video &lt;動画&gt; -name "&lt;店舗名&gt;" -n 30
            </code>
            <br />
            ブレの少ないフレームを優先しつつ、動画全体に均等に分散するよう選びます。
          </p>
        </div>
      </div>

      <div className="d-flex align-items-center gap-2 mb-2">
        <input
          className="form-control form-control-sm"
          style={{ maxWidth: 320 }}
          placeholder="店舗名・データセット名・動画名で絞り込む"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
        {filter && (
          <button className="btn btn-sm btn-outline-secondary" onClick={() => setFilter('')}>解除</button>
        )}
        <span className="text-muted small">{shown.length} / {datasets.length}件</span>
      </div>

      {loading ? (
        <div className="text-muted">読み込み中…</div>
      ) : datasets.length === 0 ? (
        <div className="alert alert-light border">まだデータセットがありません。</div>
      ) : (
        <div className="table-responsive">
          <table className="table table-sm align-middle">
            <thead className="table-light">
              <tr>
                <th>データセット</th>
                <th className="text-end">フレーム</th>
                <th>PH0 アノテータ</th>
                <th>PH1 モデル</th>
                <th>PH1.5 カバレッジ</th>
                <th className="text-end">操作</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((d) => {
                const authors = d.humanAuthors || [];
                const models = d.detectorModels || [];
                const coverage = d.playPositions > 0 ? d.detectedCount / d.playPositions : null;
                return (
                  <tr key={d.id}>
                    <td>
                      {editingId === d.id ? (
                        // Renaming in place rather than delete-and-recreate: the
                        // dataset carries the annotations, and a file that turns
                        // out to be a store's second floor must not cost them.
                        <div className="d-flex gap-1">
                          <input
                            className="form-control form-control-sm"
                            value={editingName}
                            autoFocus
                            onChange={(e) => setEditingName(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') saveName(d);
                              if (e.key === 'Escape') setEditingId(null);
                            }}
                          />
                          <button className="btn btn-sm btn-primary" onClick={() => saveName(d)}>保存</button>
                          <button className="btn btn-sm btn-outline-secondary" onClick={() => setEditingId(null)}>取消</button>
                        </div>
                      ) : (
                        <div
                          className="fw-semibold"
                          role="button"
                          title="クリックで名前を変更"
                          onClick={() => { setEditingId(d.id); setEditingName(d.name); }}
                        >
                          {d.name}
                        </div>
                      )}
                      {storeEditingId === d.id ? (
                        <div className="mt-1" style={{ maxWidth: 280 }}>
                          <StoreSearch
                            value=""
                            onPick={async (store) => {
                              await pocApi.updateDataset(d.id, {
                                name: d.name,
                                sourceVideo: d.sourceVideo || '',
                                notes: d.notes || '',
                                storeId: store.id,
                              });
                              setStoreEditingId(null);
                              load();
                            }}
                            onClear={() => setStoreEditingId(null)}
                          />
                          <button
                            className="btn btn-sm btn-link p-0 mt-1"
                            onClick={() => setStoreEditingId(null)}
                          >
                            やめる
                          </button>
                        </div>
                      ) : (
                        <div
                          className="text-muted"
                          style={{ fontSize: '0.75rem', cursor: 'pointer' }}
                          role="button"
                          title="クリックで店舗を変更"
                          onClick={() => setStoreEditingId(d.id)}
                        >
                          {d.storeName || (
                            <span className="text-danger">店舗未設定</span>
                          )}
                          {d.sourceVideo ? ` / ${d.sourceVideo}` : ''}
                        </div>
                      )}
                    </td>
                    <td className="text-end">
                      {d.frameCount}
                      <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                        完了 {d.reviewDoneCount}
                      </div>
                    </td>
                    <td>
                      {authors.length === 0 ? (
                        <span className="text-muted">未着手</span>
                      ) : (
                        authors.map((a) => (
                          <span key={a} className="badge bg-secondary-subtle text-secondary-emphasis me-1">{a}</span>
                        ))
                      )}
                      {authors.length === 1 && (
                        // The report opens for one annotator now; only the
                        // agreement rate itself needs a second person.
                        <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                          1名で確認可・一致率は2名から
                        </div>
                      )}
                    </td>
                    <td>
                      {models.length === 0 ? (
                        <span className="text-muted">未投入</span>
                      ) : (
                        models.map((m) => (
                          <span key={m} className="badge bg-info-subtle text-info-emphasis me-1">{m}</span>
                        ))
                      )}
                    </td>
                    <td>
                      {d.playPositions === 0 ? (
                        <span className="text-muted">台未登録</span>
                      ) : (
                        <>
                          <span className="fw-semibold">{d.detectedCount} / {d.playPositions}</span>
                          <span className="text-muted ms-2" style={{ fontSize: '0.75rem' }}>{percent(coverage)}</span>
                        </>
                      )}
                    </td>
                    <td className="text-end">
                      <Link className="btn btn-sm btn-outline-primary me-1" to={`/poc/${d.id}/annotate`}>
                        PH0 アノテーション
                      </Link>
                      <Link className="btn btn-sm btn-outline-secondary me-1" to={`/poc/${d.id}/report`}>
                        PH0/PH1 レポート
                      </Link>
                      <Link className="btn btn-sm btn-outline-secondary me-1" to={`/poc/${d.id}/coverage`}>
                        PH1.5 カバレッジ
                      </Link>
                      <Link className="btn btn-sm btn-outline-secondary" to={`/poc/${d.id}/tracks`}>
                        PH2 軌跡
                      </Link>
                      <button className="btn btn-sm btn-outline-danger" onClick={() => remove(d)}>
                        削除
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Layout>
  );
}
